import {
  ContentLayout,
  HeaderBreadcrumbs,
  Loader,
  PageNotFound,
} from "../../../shared";
import React from "react";
import {
  Button,
  Stack,
  Card,
  Switch,
  Typography,
  Avatar,
  FormHelperText,
  Divider,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import InputControl from "../../../shared/components/InputControl";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useParams } from "react-router-dom";
import { useCategory } from "../hooks/useCategory";
import SimpleReactValidator from "simple-react-validator";
import Avadar_img from "../../../assets/images/Icons/Avadar_img.png";
import { MultiLanguageNameTable } from "../components";

export const CreateAndEditCategory = () => {
  const [, forceUpdate] = React.useState();
  const [anchorElColorPicker, setAnchorElColorPicker] = React.useState(null);
  const [openColorPicker, setOpenColorPicker] = React.useState(
    Boolean(anchorElColorPicker),
  );
  const ref = React.useRef(null);
  const refContainer = React.useRef(null);
  const colorPickerPopperId = openColorPicker
    ? "color-picker-popper"
    : undefined;

  const validator = React.useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      validators: {
        IconFormat: {
          message: "The Image  should be png, jpg, jpeg",
          rule: (value) => {
            if (
              !["image/png", "image/jpeg", "image/jpg"].includes(value.type)
            ) {
              return false;
            }
          },
        },
        IconSize: {
          message: "The Image Size should be less than 1MB",
          rule: (value) => {
            const maxSize = 1048576; //1mb
            if (value?.size > maxSize) {
              return false;
            }
          },
        },
      },
    }),
  );

  const { id } = useParams();

  const {
    onChange,
    state,
    onSwitchChange,
    onDescription,
    onFileChange,
    onSaveCategory,
    onUpdateCategory,
  } = useCategory({ isCategory: false, categoryId: id, isProduct: false });
  const AlbumArtRef = React.useRef();

  // Effect for handle outside click of color picker
  React.useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        ref.current &&
        refContainer.current &&
        !refContainer.current.contains(event.target) &&
        !ref.current.contains(event.target)
      ) {
        setOpenColorPicker(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [ref, refContainer]);

  React.useEffect(() => {
    setOpenColorPicker(Boolean(anchorElColorPicker));
  }, [anchorElColorPicker]);

  const onLogoClick = () => {
    AlbumArtRef.current.click();
  };

  const onSubmit = () => {
    if (validator.current.allValid()) {
      id ? onUpdateCategory(id) : onSaveCategory();
    } else {
      validator.current.showMessages();
      forceUpdate(1);
    }
  };

  if (state.httpStatusCodes.details === 404) {
    return <PageNotFound />;
  }

  // Handler for open color palate
  const handleColorPickerOpen = (event) => {
    setAnchorElColorPicker(anchorElColorPicker ? null : event.currentTarget);
  };

  const multiLanguageColumns = [
    {
      head: "Language",
      accessor: "lang",
      sx: { width: "20%", minWidth: "150px" },
    },
    {
      head: "Category Name*",
      accessor: (row, index) => {
        const categoryMessage = validator.current.message(
          `${row.code}  Category Name`,
          row.locale_name,
          "required",
        );
        return (
          <InputControl
            name={`locale_name`}
            label=""
            autoComplete="off"
            onChange={(e) => onChange(e, index)}
            value={row.locale_name || ""}
            error={!!categoryMessage}
            helperText={categoryMessage}
          />
        );
      },
      sx: { width: "40%" },
    },
    {
      head: "Category Display Name*",
      accessor: (row, index) => {
        const categoryDisplayMessage = validator.current.message(
          `${row.code}  Category Display Name`,
          row.locale_display_name,
          "required",
        );
        return (
          <InputControl
            name={`locale_display_name`}
            label=""
            autoComplete="off"
            onChange={(e) => onChange(e, index)}
            value={row.locale_display_name || ""}
            error={!!categoryDisplayMessage}
            helperText={categoryDisplayMessage}
          />
        );
      },
      sx: { width: "40%" },
    },
  ];

  return (
    <>
      <ContentLayout
        title={id ? state.name : "Add Category"}
        rightContent={
          <>
            <Button
              type="button"
              size="medium"
              variant="contained"
              disabled={state.isSaveButtonBusy}
              onClick={() => onSubmit()}
            >
              {!id ? "Save" : "Update"}
            </Button>
          </>
        }
        breadcrumbs={
          <HeaderBreadcrumbs
            links={[
              { name: "Dashboard", href: "/" },
              { name: "Category", href: "/category" },
              id ? { name: state.name, href: `/category/details/${id}` } : null,
              { name: id ? "Edit" : "Create" },
            ].filter(Boolean)}
          />
        }
      >
        <Card
          sx={{
            p: "24px",
            mb: "24px",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <Typography
            sx={{ color: "gray.50" }}
            fontSize="14px"
            fontWeight={600}
          >
            DETAILS
          </Typography>

          <Stack width="100%" gap="16px">
            <Stack direction="row" gap="10px" alignItems="center">
              <Avatar
                sx={{
                  bgcolor:
                    state.data.color === null ? "#F2F2F2" : state.data.color,
                  width: "84px",
                  height: "80px",
                  borderRadius: "8px",
                  top: "10px",
                }}
                variant="rounded"
              >
                <img
                  width="50px"
                  src={
                    state.iconFile
                      ? URL.createObjectURL(state.iconFile)
                      : id
                        ? state.data.icon
                        : Avadar_img
                  }
                  alt="Category Icon"
                />
              </Avatar>

              <div>
                <input
                  id="contained-button-file"
                  type="file"
                  onChange={(e) => onFileChange(e, "image")}
                  style={{ display: "none" }}
                  ref={AlbumArtRef}
                />
                <Button
                  color="info"
                  component="span"
                  sx={{ mt: 2 }}
                  onClick={onLogoClick}
                  variant="outlined"
                  size="small"
                >
                  {id ? "Change" : "Upload"}
                </Button>
                <Typography variant="body2" color="gray.50" mt={1}>
                  Upload an image with 1:1 ratio and less than 1MB.
                </Typography>
              </div>
            </Stack>
            <Stack>
              <FormHelperText sx={{ color: "#C60808" }}>
                {validator.current.message(
                  "Image",
                  state.iconFile,
                  "IconSize|IconFormat",
                )}
              </FormHelperText>
            </Stack>
          </Stack>

          <Divider />

          <Stack gap="12px">
            <Stack gap="16px">
              <Stack gap="4px">
                <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                  Category Naming
                </Typography>
                <Typography
                  variant="caption"
                  color="gray.50"
                  sx={{ fontSize: "12px" }}
                >
                  Use Short Name for compact POS labels and Category Name for
                  customer-facing names. Rows are generated from the tenant's
                  configured languages.
                </Typography>
              </Stack>
            </Stack>

            <MultiLanguageNameTable
              columns={multiLanguageColumns}
              data={state.categoryNamings}
              loading={state.categoryNamingsLoading}
            />
          </Stack>

          <InputControl
            name="display_order"
            label="Display Order"
            autoComplete="off"
            onChange={onChange}
            value={state.data.display_order}
            required
            error={validator.current.message(
              "Display Order",
              state.data.display_order,
              "required",
            )}
            helperText={validator.current.message(
              "Display Order",
              state.data.display_order,
              "required",
            )}
          />
          <Stack direction="row" justifyContent="space-between" py="10px">
            <Typography fontSize="14px" fontWeight={500}>
              Status
            </Typography>
            <Switch
              defaultChecked={state.data.status ? true : false}
              checked={state.data.status}
              onChange={(loading) => onSwitchChange(loading, "status")}
              name={state.data.status}
              color="primary"
            />
          </Stack>
          <Divider />
          <Stack direction="row" justifyContent="space-between" py="10px">
            <Typography fontSize="14px" fontWeight={500}>
              Show in List
            </Typography>
            <Switch
              checked={state.data.show_in_list}
              onChange={(e) => onSwitchChange(e, "show_in_list")}
              name="show_in_list"
              color="primary"
            />
          </Stack>
        </Card>
        <Card sx={{ p: "24px", mb: "24px" }}>
          <Stack width={"100%"} gap="16px">
            <Typography
              sx={{ color: "gray.50" }}
              fontSize="14px"
              fontWeight={600}
            >
              DESCRIPTION
            </Typography>
            <ReactQuill
              theme="snow"
              value={state.data.description}
              onChange={onDescription}
            />
            <FormHelperText sx={{ color: "#C60808" }}>
              {validator.current.message(
                "description",
                state.data.description,
                "max:200",
              )}
            </FormHelperText>
          </Stack>
        </Card>
      </ContentLayout>
    </>
  );
};
